import { useState } from "react";
import { Clock, Check, Play } from "lucide-react";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { useLang } from "@/context/LangContext";

import { RatingPill } from "@/components/site/DestinationCard";
import { TikTokPlayer } from "@/components/site/TikTokHook";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function VideoDialog({ tour, name, onClose, onBook }) {
  const { t, money, tr } = useLang();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tour-video-dialog" className="max-w-[360px] rounded-3xl p-4">
        <DialogHeader><DialogTitle className="pr-6 text-base">{name}</DialogTitle></DialogHeader>
        <div className="overflow-hidden rounded-2xl bg-black max-h-[65vh] aspect-[9/16] mx-auto w-full" style={{ maxWidth: "calc(65vh * 9 / 16)" }}><TikTokPlayer videoId={tour.video.video_id} title={tour.video.title} /></div>
        <button data-testid="tour-video-book-btn" onClick={() => { onClose(); onBook(); }} className="w-full rounded-full bg-[#E11D48] py-3 font-bold text-white hover:bg-[#BE123C]">{t("book_now")} · {money(tour.price, tour.city_slug, 1)}{t("per_person")}</button>
      </DialogContent>
    </Dialog>
  );
}

export function LevyNote({ city, testid }) {
  const { t, lang, levyOf, plain } = useLang();
  const levy = levyOf(city);
  if (!levy) return null;
  return <p data-testid={testid} className="mt-1 rounded-lg bg-sky-50 px-2 py-1 text-[11px] font-medium leading-snug text-sky-800">{t("levy_incl").replace("{name}", levy.name[lang] || levy.name.en).replace("{amount}", plain(levy.amount))}</p>;
}

export function TourCard({ tour, onBook, i = 0 }) {
  const { t, lang, money, tr } = useLang();
  const [video, setVideo] = useState(false);
  const name = tr(tour, "name");
  const book = () => onBook({ type: "tour", slug: tour.slug, name, price: tour.price, days: tour.days, city: tour.city_slug });
  return (
    <div data-testid={`tour-package-card-${tour.slug}`} className="catalog-card group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl fade-up" style={{ animationDelay: `${i * 60}ms` }}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <ResponsiveImage src={tour.image} alt={name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-[#E11D48] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{tour.badge}</span>
        <span className="absolute bottom-4 left-4 flex items-center gap-1 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur"><Clock className="h-3.5 w-3.5" />{tour.days} {tour.days === 1 ? t("day_one") : t("days")}</span>
        {tour.video && (
          <button data-testid={`tour-video-btn-${tour.slug}`} onClick={() => setVideo(true)} className="absolute bottom-4 right-4 flex min-h-11 items-center gap-1.5 rounded-full bg-white/95 py-1.5 pl-1.5 pr-3 text-xs font-bold text-slate-900 shadow-lg transition-transform hover:scale-105">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E11D48]"><Play className="h-3 w-3 fill-white text-white" /></span>{t("watch_video")}
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <RatingPill rating={tour.rating} count={tour.review_count} />
        <h3 className="mt-2 text-lg font-bold text-slate-900">{name}</h3>
        <p className="mt-1 text-sm text-slate-500">{tour.highlights.join(" · ")}</p>
        <ul className="mt-3 space-y-1">
          {tr(tour, "includes").slice(0, 3).map((x) => <li key={x} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 shrink-0 text-emerald-600" />{x}</li>)}
        </ul>
        <div className="catalog-price-actions mt-auto flex items-end justify-between gap-3 pt-5">
          <div className="min-w-0"><p className="text-xs text-slate-500">{t("from")}</p><p data-testid={`tour-price-${tour.slug}`} className="catalog-price text-xl font-extrabold text-slate-900">{money(tour.price, tour.city_slug, 1)}<span className="text-sm font-medium text-slate-500">{t("per_person")}</span></p></div>
          <button data-testid={`tour-book-now-button-${tour.slug}`} onClick={book}
            className="shrink-0 whitespace-nowrap rounded-full bg-[#E11D48] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#BE123C]">{t("book_now")}</button>
        </div>
        <LevyNote city={tour.city_slug} testid={`tour-levy-note-${tour.slug}`} />
      </div>
      {video && <VideoDialog tour={tour} name={name} onClose={() => setVideo(false)} onBook={book} />}
    </div>
  );
}
